/*
  RS search fallback: loads Dawn's predictive search only when no search app gives suggestions.
  Config comes from snippets/rs-search-app.liquid (window.rsSearchFallback).
  - No app configured, or the app script failed to load: the theme's suggestions are turned on at
    once, also for the inline header field (typing there opens the search box). A load failure is
    remembered for 15 minutes.
  - App loaded: on a search of 3+ characters the script waits a few seconds. If the app has not
    built its suggestion list (Searchanise: .snize-ac-results) anywhere on the page, the theme's
    suggestions are added to the search box for this page only. The app's own fields are left alone,
    and if the app answers later only its list is shown.
  - No app (or it failed to load): the search forms go to Shopify's search page instead of the
    Searchanise results page.
*/
(() => {
  const config = window.rsSearchFallback;
  if (!config || window.rsSearchFallbackReady) return;
  window.rsSearchFallbackReady = true;

  const WAIT = 3000;
  const MIN_CHARS = 3;
  const MEMORY_KEY = 'rsSearchAppFailedUntil';
  const MEMORY_TIME = 15 * 60 * 1000;
  const APP_LIST = '.snize-ac-results, [class*="snize-ac"]';
  const root = document.documentElement;

  // 'full': no app at all; 'box': app loaded but silent, theme suggestions in the search box only
  let mode = null;
  let checking = false;

  try {
    // Flag from the first version of this script, which could be set by mistake
    sessionStorage.removeItem('rsSearchFallbackUntil');
  } catch (error) {
    // Storage blocked
  }

  const appFailedRecently = () => {
    try {
      return Number(sessionStorage.getItem(MEMORY_KEY)) > Date.now();
    } catch (error) {
      return false;
    }
  };

  const rememberAppFailed = () => {
    try {
      sessionStorage.setItem(MEMORY_KEY, String(Date.now() + MEMORY_TIME));
    } catch (error) {
      // Storage blocked: the check simply runs again on the next page
    }
  };

  // The app counts as working once its suggestion list exists with some content, visible or not
  const appAnswered = () =>
    Array.from(document.querySelectorAll(APP_LIST)).some((element) => element.textContent.trim() !== '');

  // No search app: the magnifier and Enter go to Shopify's search page instead of the Searchanise
  // results page (snippets/rs-search-url.liquid).
  const useShopifySearchPage = () => {
    window.rsSearchAppFailed = true;
    document.querySelectorAll('form[data-rs-search-action]').forEach((form) => {
      form.removeAttribute('data-rs-search-action');
    });
  };

  const activate = (newMode) => {
    if (mode === 'full' || mode === newMode) return;
    mode = newMode;
    root.classList.add('rs-search-fallback');
    if (mode === 'full') useShopifySearchPage();

    if (customElements.get('predictive-search')) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = config.css;
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = config.js;
    script.onload = () => {
      // Show suggestions for what is already typed in an open search box
      const input = document.querySelector('dialog[open] predictive-search input[name="q"]');
      if (input && input.value.trim()) {
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    };
    document.body.appendChild(script);
  };

  const check = () => {
    if (mode || checking) return;
    if (window.rsSearchAppFailed) {
      rememberAppFailed();
      activate('full');
      return;
    }
    checking = true;
    setTimeout(() => {
      checking = false;
      if (appAnswered()) return;
      activate('box');
    }, WAIT);
  };

  const openSearchBox = (field) => {
    const modal = window.rsSearchModal;
    if (!modal) return;
    const dialog = modal.find(field);
    if (!dialog || dialog.open) return;
    const input = modal.open(dialog, field.value);
    if (input) input.dispatchEvent(new Event('input', { bubbles: true }));
  };

  document.addEventListener('input', (event) => {
    const field = event.target;
    if (!(field instanceof HTMLInputElement) || field.name !== 'q') return;

    if (mode === 'full') {
      if (field.classList.contains('rs-search__input')) openSearchBox(field);
      return;
    }

    if (mode === 'box') {
      // The app came back: hide the theme's list so only one shows
      setTimeout(() => {
        if (appAnswered()) root.classList.add('rs-search-app-answered');
      }, 1500);
      return;
    }

    if (field.value.trim().length >= MIN_CHARS) check();
  });

  if (!config.app || window.rsSearchAppFailed || appFailedRecently()) activate('full');
})();
